import React, { useState, useEffect } from 'react';
import { motion, useAnimation } from 'framer-motion';
import {
  Mail,
  Phone,
  MapPin,
  Instagram,
  Facebook,
  Twitter,
  Linkedin,
  ArrowRight,
} from 'lucide-react';
import { Link } from 'react-router-dom';

const Footer = () => {
  const controls = useAnimation();
  const [hasAnimated, setHasAnimated] = useState(false);

  useEffect(() => {
    const footer = document.getElementById('animated-footer');
    if (!footer) return;

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          controls.start('visible');
          setHasAnimated(true);
        }
      },
      { threshold: 0.2 }
    );

    observer.observe(footer);
    return () => observer.disconnect();
  }, [controls, hasAnimated]);

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: { staggerChildren: 0.15, delayChildren: 0.3 },
    },
  };

  const itemVariants = {
    hidden: { y: 30, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: { duration: 0.5, ease: 'easeOut' },
    },
  };

  const socialLinks = [
    { icon: Instagram, url: 'https://www.instagram.com/' },
    { icon: Facebook, url: 'https://www.facebook.com/' },
    { icon: Twitter, url: 'https://twitter.com/' },
    { icon: Linkedin, url: 'https://www.linkedin.com/' },
  ];

  return (
    <footer
      id='animated-footer'
      className='relative bg-gradient-to-br from-gray-900 via-gray-800 to-gray-900 text-white pt-16 pb-8'
    >
      <motion.div
        className='container mx-auto px-4 pointer-events-auto select-text'
        variants={containerVariants}
        initial='hidden'
        animate={controls}
      >
        <div className='grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10'>
          {/* Company Info */}
          <motion.div variants={itemVariants}>
            <h3 className='text-2xl font-bold mb-4 text-emerald-400'>
              Pralhad Flours
            </h3>
            <p className='text-gray-300 mb-6'>
              Traditinal way Healthy  flour !
            </p>
            <div className='flex gap-4'>
              {socialLinks?.map(({ icon: Icon, url }, i) => (
                <a
                  key={i}
                  href={url}
                  target='_blank'
                  rel='noopener noreferrer'
                  className='hover:text-emerald-400 transition-colors'
                >
                  <Icon size={18} />
                </a>
              ))}
            </div>
          </motion.div>

          {/* Quick Links */}
          <motion.div variants={itemVariants}>
            <h4 className='font-bold text-lg mb-4'>Quick Links</h4>
            <ul className='space-y-2 text-gray-300'>
              {[
                ['Home', '/'],
                ['Products', '/products'],
                ['Contact', '/contact'],
                ['About Us', '/aboutus'],
                // ['Blog', '/blog'],
                // ['Cart', '/cart'],
              ].map(([name, path], i) => (
                <li key={i}>
                  <Link
                    to={path}
                    className='hover:text-emerald-400 transition-colors flex items-center'
                  >
                    <ArrowRight size={14} className='mr-2' /> {name}
                  </Link>
                </li>
              ))}
            </ul>
          </motion.div>

          {/* Contact Info */}
          <motion.div variants={itemVariants}>
            <h4 className='font-bold text-lg mb-4'>Contact</h4>
            <ul className='space-y-3 text-gray-300'>
              <li className='flex'>
                <MapPin size={18} className='mr-2 mt-1' />
                Godrej Green Cove, Tower No 3, Flat no - 905 <br />
                Nande Balewadi Road, Mahalunge, Pune 411045
              </li>
              <li className='flex'>
                <Mail size={18} className='mr-2' />
                <a
                  href='mailto:pralhadflours@gmail.com'
                  className='hover:text-emerald-400'
                >
                  pralhadflours@gmail.com
                </a>
              </li>
              <li className='flex'>
                <Phone size={18} className='mr-2' />
                <a
                  href='tel:9765660423'
                  className='hover:text-emerald-400'
                >
                  9765660423
                </a>
              </li>
            </ul>
          </motion.div>
        </div>

        <motion.div
          className='border-t border-gray-700 mt-10 pt-6 text-sm text-gray-400 flex flex-col md:flex-row justify-between'
          variants={itemVariants}
        >
          <p>
            &copy; {new Date().getFullYear()} Pralhad Flours. All
            rights reserved.
          </p>
          <div className='flex gap-4 mt-4 md:mt-0'>
            <a className='hover:text-emerald-400'>Privacy</a>
            <a className='hover:text-emerald-400'>Terms</a>
            <a className='hover:text-emerald-400'>Sitemap</a>
          </div>
        </motion.div>
      </motion.div>
    </footer>
  );
};

export default Footer;
